import { notify } from "@general-backoffice/core/index";
import type { UpcomingCharge, UpcomingChargesResponse, UpcomingChargesSummary } from "@investhinks/api-client";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";

import { upcomingChargesEndpoints } from "../../../../api";
import NumberStatsCard from "../../../../components/shared/NumberStatsCard";
import BillingOwnershipStatus, { isBillingValidationPending } from "../../../../components/charges/BillingOwnershipStatus";
import BillingTotalStatsCard from "../../../../components/charges/BillingTotalStatsCard";
import { summarizeUpcomingChargeAmounts } from "../../../../components/charges/upcomingChargeTotals";
import { clearCache, getFromCache, setToCache } from "../../../../utils/memoryCache";
import { TypedTable } from "../../../../utils/typedLib";

type CacheData = UpcomingChargesResponse;

const CACHE_KEY = "upcomingCharges";
const CACHE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

const getCache = (): CacheData | null => {
  const cached = getFromCache<CacheData>(CACHE_KEY);
  if (!cached) return null;
  if (cached.age > CACHE_TIMEOUT_MS) {
    clearCache(CACHE_KEY);
    return null;
  }
  return cached.data;
};

const setCache = (data: CacheData) => {
  setToCache(CACHE_KEY, data);
};

const clearUpcomingChargesCache = () => {
  clearCache(CACHE_KEY);
};

const formatCurrency = (amount: number, currency: string = "EUR"): string => {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency,
  }).format(amount);
};

/** Must match UPCOMING_WINDOW_DAYS in the admin overview. */
const FAILED_SUBSCRIPTION_WINDOW_DAYS = 7;

const UpcomingCharges: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "upcomingCharges" });
  const [isLoading, setIsLoading] = useState(true);
  const [charges, setCharges] = useState<UpcomingCharge[]>([]);
  const [hasLoadedCharges, setHasLoadedCharges] = useState(false);
  const [summary, setSummary] = useState<UpcomingChargesSummary>({
    totalCharges: 0,
    totalEstimatedAmount: 0,
    totalExtrapolatedAmount: 0,
    currency: "EUR",
  });
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const amountTotals = useMemo(() => summarizeUpcomingChargeAmounts(charges, summary.currency), [charges, summary.currency]);

  // Deep link from the admin overview: ?filter=failedSubscription narrows the
  // table to the charges that panel was counting.
  const activeFilter = searchParams.get("filter");
  const setFilter = (value: string | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("filter", value);
        else next.delete("filter");
        return next;
      },
      { replace: true }
    );

  const visibleCharges = useMemo(() => {
    if (activeFilter === "failedSubscription")
      // Must match what the admin overview counted, window included, or the
      // list shows more rows than the badge promised.
      return charges.filter(
        (c) => c.subscription?.pm_status !== "active" && c.billing.daysUntilDue <= FAILED_SUBSCRIPTION_WINDOW_DAYS
      );
    return charges;
  }, [charges, activeFilter]);

  const loadCharges = useCallback(
    async (forceRefresh: boolean = false) => {
      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cached = getCache();
        if (cached) {
          setCharges(cached.charges || []);
          setHasLoadedCharges(true);
          setSummary(
            cached.summary || { totalCharges: 0, totalEstimatedAmount: 0, totalExtrapolatedAmount: 0, currency: "EUR" }
          );
          setIsLoading(false);
          return;
        }
      } else {
        clearUpcomingChargesCache();
      }

      setIsLoading(true);
      try {
        const result = await upcomingChargesEndpoints.getUpcomingCharges({ daysAhead: 0 });
        setCharges(result.charges || []);
        setHasLoadedCharges(true);
        setSummary(
          result.summary || {
            totalCharges: 0,
            totalEstimatedAmount: 0,
            totalExtrapolatedAmount: 0,
            currency: "EUR",
          }
        );
        setCache(result);
      } catch (error: unknown) {
        console.error("Error loading upcoming charges:", error);
        notify.error(t("loadError"));
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadCharges(false);
  }, [loadCharges]);

  const handleDownloadPreview = async (charge: UpcomingCharge) => {
    if (isBillingValidationPending(charge.estimate) || charge.status === "not_due") return;
    const chargeId = `${charge.subscription.id}-${charge.billing.periodStart}`;
    setDownloadingId(chargeId);

    try {
      const blob = await upcomingChargesEndpoints.downloadPreviewInvoice({
        pm_amount: charge.estimatedAmount,
        pm_currency: charge.currency,
        pm_reason: charge.billing.periodReason,
        pm_recipientBillingData: charge.user.pm_billingData,
        bot_config: {
          exchange_account: {
            name: charge.botConfig.exchange_account.name,
          },
        },
        lineItems: charge.estimate.lineItems,
        baseAmount: charge.estimate.baseAmount,
        discountedAmount: charge.estimate.discountedAmount,
        previewId: `${charge.subscription.id}`,
      });

      // Open PDF in new window
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      notify.success(t("previewSuccess"));
    } catch (error: unknown) {
      console.error("Error downloading preview:", error);
      notify.error(t("previewError"));
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    {
      name: t("columns.user"),
      formattedContent: ({ user }: UpcomingCharge) => (
        <>
          <div className="font-weight-bold">{user.name || "-"}</div>
          <small className="text-muted">{user.email}</small>
        </>
      ),
      isTh: true,
    },
    {
      name: t("columns.botAccount"),
      formattedContent: ({ botConfig, user }: UpcomingCharge) => !user.id || !botConfig.exchange_account.id ? <span>{t("accountLinkMissing")}</span> : (
        <Link
          to={`/users/${user.id}/exchange-account/${botConfig.exchange_account.id}`}
          className="text-primary font-weight-bold"
        >
          {botConfig.exchange_account.name || "-"}
        </Link>
      ),
    },
    {
      name: t("columns.period"),
      formattedContent: ({ billing }: UpcomingCharge) => (
        <>
          <div>{moment(billing.periodStart).format("DD/MM/YYYY")}</div>
          <small className="text-muted">
            {t("columns.periodTo")} {moment(billing.periodEnd).format("DD/MM/YYYY")}
          </small>
        </>
      ),
    },
    {
      name: t("columns.dueDate"),
      formattedContent: ({ billing }: UpcomingCharge) => {
        const dueDate = moment(billing.dueDate);
        const daysUntil = dueDate.diff(moment(), "days");

        let badgeColor: string, badgeText: string;
        if (daysUntil < 0) {
          badgeColor = "danger";
          badgeText = String(t("daysOverdue", { days: Math.abs(daysUntil) }));
        } else if (daysUntil === 0) {
          badgeColor = "danger";
          badgeText = t("dueToday");
        } else if (daysUntil <= 7) {
          badgeColor = "warning";
          badgeText = t("daysLeft", { days: daysUntil });
        } else {
          badgeColor = "info";
          badgeText = t("daysLeft", { days: daysUntil });
        }

        return (
          <>
            <div>{dueDate.format("DD/MM/YYYY")}</div>
            <Badge color={badgeColor}>{badgeText}</Badge>
          </>
        );
      },
    },
    {
      name: t("columns.billing"),
      formattedContent: ({ billing, estimate, status, statusReason }: UpcomingCharge) => status === "not_due" ? (
        <div><Badge color="secondary">{t("noChargeDue")}</Badge><div className="small text-muted">{t(statusReason === "fully_free_period" ? "fullyFreePeriod" : "noActivityObserved")}</div></div>
      ) : isBillingValidationPending(estimate) ? <BillingOwnershipStatus estimate={estimate} /> : (
        <>
          <div>
            <span className="text-muted">{t("profit")}: </span>
            {formatCurrency(billing.periodProfit)}
          </div>
          <div>
            <span className="text-muted">{t("fee")}: </span>
            {(billing.feePercentage * 100).toFixed(0)}%
          </div>
          <BillingOwnershipStatus estimate={estimate} />
          {billing.remainingFreeDays > 0 && (
            <Badge color="success" className="mt-1">
              {t("remainingFreeDays", { days: billing.remainingFreeDays })}
            </Badge>
          )}
        </>
      ),
    },
    {
      name: t("columns.estAmount"),
      formattedContent: (row: UpcomingCharge) => {
        const { estimate, estimatedAmount, currency } = row;
        if (row.status === "not_due") return <Badge color="secondary">{t("noChargeDue")}</Badge>;
        if (isBillingValidationPending(estimate)) return <Badge color="warning">{t("validationPending")}</Badge>;
        const tooltipId = `amount-${row.subscription.id}`;

        return (
          <>
            <div className="font-weight-bold text-primary" id={tooltipId}>
              {formatCurrency(estimatedAmount, currency)}
            </div>
            <UncontrolledTooltip target={tooltipId} placement="left">
              <div className="text-left">
                {estimate.lineItems.map((item, i) => (
                  <div key={i} className={item.type === "discount" ? "text-success" : ""}>
                    {item.description}: {formatCurrency(item.finalAmount, currency)}
                  </div>
                ))}
              </div>
            </UncontrolledTooltip>
            {estimate.discountedAmount > 0 && (
              <small className="text-success">
                <del>{formatCurrency(estimate.baseAmount, currency)}</del>
              </small>
            )}
          </>
        );
      },
    },
    {
      name: t("columns.actions"),
      formattedContent: (row: UpcomingCharge) => {
        const chargeId = `${row.subscription.id}-${row.billing.periodStart}`;
        const isDownloading = downloadingId === chargeId;

        return (
          <Button color="primary" size="sm" onClick={() => handleDownloadPreview(row)} disabled={isDownloading || isBillingValidationPending(row.estimate) || row.status === "not_due"}>
            {isDownloading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <i className="fas fa-file-invoice mr-1" />
                {t("preview")}
              </>
            )}
          </Button>
        );
      },
    },
  ];

  return (
    <Container className="mt-4" fluid>
      <Row className="mb-4">
        <Col xs={12} md={6} xl={4}>
          <NumberStatsCard
            color="warning"
            isDark={true}
            number={charges.length}
            displayValue={!hasLoadedCharges ? t("summaryUnavailable") : undefined}
            decimals={0}
            suffix={` ${t("statsCharges.suffix")}`}
            title={t("statsCharges.title")}
            icon="fas fa-calendar-alt"
            description={t("statsCharges.descriptionAll")}
            isLoading={isLoading}
          />
        </Col>
        <Col xs={12} md={6} xl={4}>
          <BillingTotalStatsCard
            color="success"
            verifiedAmount={amountTotals.verifiedEstimatedAmount}
            verifiedCount={amountTotals.verifiedCount}
            pendingCount={amountTotals.pendingCount}
            currency={summary.currency}
            hasLoaded={hasLoadedCharges}
            title={t("statsProfitsSoFar.title")}
            icon="fas fa-euro-sign"
            description={t("statsProfitsSoFar.description")}
            isLoading={isLoading}
          />
        </Col>
        <Col xs={12} md={6} xl={4}>
          <BillingTotalStatsCard
            color="info"
            verifiedAmount={amountTotals.verifiedExtrapolatedAmount}
            verifiedCount={amountTotals.verifiedCount}
            pendingCount={amountTotals.pendingCount}
            currency={summary.currency}
            hasLoaded={hasLoadedCharges}
            title={t("statsExtrapolated.title")}
            icon="fas fa-chart-line"
            description={t("statsExtrapolated.description")}
            isLoading={isLoading}
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Alert color="info" className="mb-0">
            <i className="fas fa-info-circle mr-2"></i>
            {t("extrapolationDisclaimer")}
          </Alert>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card className="shadow">
            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
              <div>
                <h3 className="mb-0">{t("title")}</h3>
                <small className="text-muted">{t("subtitle")}</small>
              </div>
              <div className="d-flex align-items-center" style={{ gap: ".5rem" }}>
                {activeFilter === "failedSubscription" && (
                  <Button color="warning" size="sm" outline onClick={() => setFilter(null)}>
                    <i className="fas fa-filter mr-1" aria-hidden="true" />
                    {t("filters.failedSubscription")}
                    <i className="fas fa-times ml-2" aria-hidden="true" />
                  </Button>
                )}
                <Button color="secondary" size="sm" onClick={() => loadCharges(true)} disabled={isLoading}>
                  <i className="fas fa-sync-alt mr-1" aria-hidden="true" />
                  {t("refresh")}
                </Button>
              </div>
            </CardHeader>
            <CardBody className="p-0" style={{ position: "relative", minHeight: isLoading ? "200px" : "auto" }}>
              {isLoading && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                  }}
                >
                  <Spinner color="primary" />
                  <div className="mt-3 text-muted">{t("loading")}</div>
                </div>
              )}
              <TypedTable
                columns={columns}
                rows={visibleCharges}
                isLoading={isLoading}
                noContentMessage={t("noCharges")}
                rowAmount={10}
                selectable={false}
                sortable={false}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UpcomingCharges;
